import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { AccountOffer, Amount, BookOffer } from 'xrpl';
import { Token, TokenCode } from '../models/tokens';
import { isString } from './typeof-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Converts a Unix timestamp to an ISO string
 * @param timestamp Unix timestamp in seconds
 * @returns ISO 8601 formatted date string
 */
export function timestampToISOString(timestamp: number): string {
  return new Date(timestamp * 1000).toISOString();
}

// export function getPairDecimals(network: NETWORK, baseId: string, quoteId: string): number {
//   const tokens = TOKEN_MAP[network] || [];
//   const baseToken = tokens.find((token: Token) => token.id === baseId);
//   if (!baseToken) return 2;

//   const pair = baseToken.pairs.find((p: { quote: string }) => p.quote === quoteId);
//   return pair?.decimals ?? 2;
// }

export function getNumericValue(
  amount: string | { currency: string; issuer: string; value: string }
): number {
  if (typeof amount === 'string') {
    return Number(amount) / 1000000; // Convert XRP drops to XRP
  }
  return Number(amount.value);
}

// export function formatAmount(
//   amount: string | { currency: string; issuer: string; value: string | number },
//   network: NETWORK,
//   baseId: string,
//   quoteId: string,
//   decimals?: number,
//   includeCurrency: boolean = false
// ): string {
//   const displayDecimals = decimals ?? getPairDecimals(network, baseId, quoteId);
//   if (typeof amount === 'string') {
//     const formatted = `${(parseInt(amount) / 1000000).toFixed(displayDecimals)}`;
//     return includeCurrency ? `${formatted} XRP` : formatted;
//   }
//   const formatted = `${Number(amount.value).toFixed(displayDecimals)}`;
//   if (includeCurrency) {
//     const normalizedCurrency = normalizeCurrencyCode(amount.currency);
//     return `${formatted} ${normalizedCurrency || amount.currency}`;
//   }
//   return formatted;
// }

/**
 * Get the type of an offer for a given pair
 *
 * @deprecated Use getOfferSide instead
 */
export function getOfferType(
  offer: {
    TakerGets?: string | { currency: string; issuer: string; value: string };
    TakerPays?: string | { currency: string; issuer: string; value: string };
    taker_gets?: string | { currency: string; issuer: string; value: string };
    taker_pays?: string | { currency: string; issuer: string; value: string };
  },
  baseToken: Token | undefined,
  quoteToken: Token | undefined
): 'buy' | 'sell' | null {
  if (!baseToken || !quoteToken) return null;

  const gets = offer.TakerGets || offer.taker_gets;
  const pays = offer.TakerPays || offer.taker_pays;

  if (!gets || !pays) return null;

  const isBaseTokenInGets =
    typeof gets === 'object'
      ? gets.currency === baseToken.code && gets.issuer === baseToken.issuer
      : baseToken.code === 'XRP';

  const isBaseTokenInPays =
    typeof pays === 'object'
      ? pays.currency === baseToken.code && pays.issuer === baseToken.issuer
      : baseToken.code === 'XRP';

  const isQuoteTokenInGets =
    typeof gets === 'object'
      ? gets.currency === quoteToken.code && gets.issuer === quoteToken.issuer
      : quoteToken.code === 'XRP';

  const isQuoteTokenInPays =
    typeof pays === 'object'
      ? pays.currency === quoteToken.code && pays.issuer === quoteToken.issuer
      : quoteToken.code === 'XRP';

  if (isBaseTokenInGets && isQuoteTokenInPays) return 'sell';
  if (isBaseTokenInPays && isQuoteTokenInGets) return 'buy';
  return null;
}

/**
 * Classify an offer for a given pair
 * @param offer AccountOffer or BookOffer
 * @param base Base token
 * @param quote Quote token
 * @returns 'buy' | 'sell' | 'unknown'
 */
export const getOfferSide = (
  offer: AccountOffer | BookOffer,
  baseCurrency: TokenCode | undefined,
  quoteCurrency: TokenCode | undefined
) => {
  if (!baseCurrency || !quoteCurrency) return null;

  const getCurrency = (amt: Amount) => (isString(amt) ? 'XRP' : amt.currency);

  const takerGets = 'taker_gets' in offer ? offer.taker_gets : offer.TakerGets;
  const takerPays = 'taker_pays' in offer ? offer.taker_pays : offer.TakerPays;

  const gets = getCurrency(takerGets);
  const pays = getCurrency(takerPays);

  if (gets === baseCurrency && pays === quoteCurrency) return 'sell';
  if (gets === quoteCurrency && pays === baseCurrency) return 'buy';
  return null;
};
